import { Client } from '@stomp/stompjs';
import { SaveCouponRequest } from './types';
import { couponStorageService } from '../storage/couponStorage';
import {CouponForm} from "../../models/api/codegen/code";

class CouponWsService {
    private client: Client | null = null;
    private wsQueueId: string | null = null;

    constructor() {
        console.log(new Date(), "[COUPON WS] coupon constructor")
        const savedData = couponStorageService.get();
        if (savedData && savedData.wsQueueId) {
            this.wsQueueId = savedData.wsQueueId;
        } else {
            // Если нет сохраненных данных, генерируем новый ID
            this.wsQueueId = crypto.randomUUID();
        }
    }

    /**
     * Получение WebSocket URL из конфигурации
     */
    private getBrokerURL(): string {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
        return `${wsProtocol}://${window.location.host}/spirs-coupon-ws`;
    }

    getWsQueueId(): string {
        const result = this.wsQueueId ? this.wsQueueId : 'no-id';
        return result;
    }

    resetWsQueueId(): string {
        this.wsQueueId = crypto.randomUUID();
        return this.wsQueueId;
    }

    setWsQueueId(queueId: string): void {
        this.wsQueueId = queueId;
    }

    connect(onMessage: (msg: any) => void): Promise<void> {
        if (this.client && this.client.connected) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            const brokerURL = this.getBrokerURL();

            this.client = new Client({
                brokerURL: brokerURL,
                reconnectDelay: 5000,
                debug: str => console.log('STOMP:', str)
            });

            this.client.onConnect = frame => {
                this.client?.subscribe(
                    `/queue/coupon-${this.wsQueueId}`,
                    message => {
                        const body = JSON.parse(message.body);
                        onMessage(body);
                    }
                );

                resolve();
            };

            this.client.onStompError = frame => {
                reject(
                    new Error(
                        `STOMP Error: ${frame.headers?.message ||
                            'Unknown error'}`
                    )
                );
            };

            this.client.onWebSocketClose = () => {
                console.log(
                    '[CouponWsService] 🔌 WebSocket соединение закрыто'
                );
            };

            this.client.onWebSocketError = error => {
                reject(new Error(`WebSocket Error: ${error}`));
            };

            // Таймаут на подключение (5 секунд)
            const connectionTimeout = setTimeout(() => {
                reject(new Error('Connection timeout'));
            }, 5000);

            // Очищаем таймаут при успешном подключении или ошибке
            const originalResolve = resolve;
            const originalReject = reject;

            resolve = (...args) => {
                clearTimeout(connectionTimeout);
                originalResolve(...args);
            };

            reject = (...args) => {
                clearTimeout(connectionTimeout);
                originalReject(...args);
            };

            this.client.activate();
        });
    }

    sendSaveCoupon(couponForm: CouponForm) {
        if (!this.client) {
            console.error('[CouponWsService] Клиент не инициализирован');
            return;
        }

        if (!this.client.connected) {
            console.error(
                '[CouponWsService] Клиент не подключен к WebSocket'
            );
            return;
        }

        // правильный payload с wsQueueId
        const saveRequest: SaveCouponRequest = {
            wsQueueId: this.wsQueueId || '',
            couponForm: couponForm
        };

        this.client.publish({
            destination: '/app/save-coupon',
            body: JSON.stringify(saveRequest)
        });
    }

    sendCheckCoupon(couponForm: any) {
        if (!this.client) {
            console.error('[CouponWsService] Клиент не инициализирован');
            return;
        }

        if (!this.client.connected) {
            console.error(
                '[CouponWsService] Клиент не подключен к WebSocket'
            );
            return;
        }

        this.client.publish({
            destination: '/app/check-coupon',
            body: JSON.stringify(couponForm)
        });
    }

    /**
     * Закрытие WebSocket соединения
     */
    disconnect(): void {
        if (this.client && this.client.connected) {
            try {
                this.client.deactivate();
            } catch (error) {
                console.error(
                    '[CouponWsService] Ошибка при закрытии WebSocket:',
                    error
                );
            }
        } else {
            console.log(
                '[CouponWsService]  WebSocket соединение уже закрыто или не было установлено'
            );
        }

        this.client = null;
    }

    /**
     * Проверка состояния подключения
     */
    isConnected(): boolean {
        return this.client?.connected || false;
    }

    /**
     * Получение текущего WebSocket URL (для отладки)
     */
    getCurrentBrokerURL(): string {
        return this.getBrokerURL();
    }
}

export const couponWsService = new CouponWsService();
